import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Zap, Clock } from "lucide-react";
import axios from "axios";
import { timeAgo, formatDollars } from "@/lib/language";
import { staggerContainer, staggerItem } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivityTimeline() {
  const [events, setEvents] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/savings/activity?limit=10`).then(r => setEvents(r.data)).catch(() => {});
  }, []);

  if (events.length === 0) return null;

  const visible = showAll ? events : events.slice(0, 5);

  return (
    <div className="space-y-3" data-testid="activity-timeline">
      <h3 className="text-sm font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider">
        Recent Activity
      </h3>
      <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-2">
        {visible.map((ev, i) => (
          <motion.div key={ev.id || i} variants={staggerItem}>
            <Card
              className="card-themed cursor-pointer transition-all"
              onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}
              data-testid={`activity-item-${i}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-[var(--qn-foreground)]">{ev.message}</p>
                    <span className="text-xs text-[var(--qn-foreground-subtle)]">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ""} ({timeAgo(ev.timestamp)})
                    </span>
                  </div>
                  {ev.details && (
                    expanded === ev.id
                      ? <ChevronUp className="w-4 h-4 text-[var(--qn-foreground-subtle)] shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-subtle)] shrink-0" />
                  )}
                </div>
                <AnimatePresence>
                  {expanded === ev.id && ev.details && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 pt-3 border-t border-[var(--qn-border)] grid grid-cols-3 gap-3 text-center">
                        <div>
                          <div className="text-xs text-[var(--qn-foreground-subtle)]">Saved</div>
                          <div className="text-sm font-mono font-bold text-[var(--qn-success)]">
                            {formatDollars(ev.details.dollars_saved)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-[var(--qn-foreground-subtle)]">Cost Cut</div>
                          <div className="text-sm font-mono font-bold">{ev.details.cost_reduction_pct}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-[var(--qn-foreground-subtle)]">Faster</div>
                          <div className="text-sm font-mono font-bold text-[var(--qn-primary)]">{ev.details.time_reduction_pct}%</div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
      {events.length > 5 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAll(!showAll)}
          className="text-[var(--qn-foreground-muted)]"
          data-testid="activity-view-all"
        >
          {showAll ? "Show Less" : `View All (${events.length})`}
        </Button>
      )}
    </div>
  );
}
